import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts';

interface Props {
  onClose: () => void;
  onStart: (language: 'french' | 'arabic' | 'english') => void;
  starting: boolean;
  attemptedLanguages?: string[];  // Languages already attempted by user
}

const InstructionsModal: React.FC<Props> = ({ onClose, onStart, starting, attemptedLanguages = [] }) => {
  const { t } = useTranslation();
  const { isRTL: globalIsRTL } = useLanguage();
  const [testLanguage, setTestLanguage] = useState<'french' | 'arabic' | 'english'>('french');

  const borderSide = globalIsRTL ? 'border-r-4' : 'border-l-4';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div
        className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6"
        dir={globalIsRTL ? 'rtl' : 'ltr'}
      >
        {/* Header */}
        <div className="flex justify-between items-center mb-5">
          <h2 className="text-xl font-bold">
            <span className="text-primary">WordFlow Challenge</span>
            <span className="text-gray-700"> - {t('instructions.title')}</span>
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">
            &times;
          </button>
        </div>

        {/* Test Language Picker */}
        <div className="mb-5">
          <div className="flex gap-2 justify-center mb-3">
            {(['french', 'arabic', 'english'] as const).map((lang) => {
              const isAttempted = attemptedLanguages.includes(lang);
              return (
                <button
                  key={lang}
                  type="button"
                  onClick={() => !isAttempted && setTestLanguage(lang)}
                  disabled={isAttempted}
                  className={`py-2 px-3 rounded-lg text-lg font-semibold border transition ${
                    isAttempted
                      ? 'bg-gray-200 text-gray-400 border-gray-300 cursor-not-allowed opacity-50'
                      : testLanguage === lang
                      ? 'bg-primary text-white border-primary'
                      : 'bg-white text-gray-600 border-gray-300 hover:border-primary'
                  }`}
                  title={isAttempted ? `${lang} test already completed` : ''}
                >
                  {lang === 'french' ? '🇫🇷' : lang === 'arabic' ? '🇩🇿' : '🇬🇧'}
                </button>
              );
            })}
          </div>
          <p className="text-sm text-gray-600 text-center">
            {t('instructions.testLanguageMessage')} <span className="font-semibold text-primary">
              {testLanguage === 'french' ? t('languages.french') : testLanguage === 'arabic' ? t('languages.arabic') : t('languages.english')}
            </span>
          </p>
        </div>

        <div className="space-y-4 text-sm">
          {/* How to Answer */}
          <div className={`bg-blue-50 p-4 rounded-lg ${borderSide} border-blue-500`}>
            <h3 className="font-bold text-blue-900 mb-3">{t('instructions.howToAnswer')}</h3>
            <div className="space-y-2">
              {(t('instructions.howToAnswerItems', { returnObjects: true }) as string[]).map((item, i) => (
                <div key={i} className={`flex items-start gap-2 ${globalIsRTL ? 'flex-row-reverse text-right' : ''}`}>
                  <span className="text-blue-600">•</span>
                  <p className="text-blue-900 text-sm">{item}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Before Starting */}
          <div className={`bg-green-50 p-4 rounded-lg ${borderSide} border-green-500`}>
            <h3 className="font-bold text-green-900 mb-3">{t('instructions.beforeStarting')}</h3>
            <div className="space-y-2">
              {(t('instructions.beforeStartingItems', { returnObjects: true }) as string[]).map((item, i) => (
                <div key={i} className={`flex items-start gap-2 ${globalIsRTL ? 'flex-row-reverse text-right' : ''}`}>
                  <span className="text-green-600">✓</span>
                  <p className="text-green-900 text-sm">{item}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Results Info */}
          <div className={`bg-amber-50 p-4 rounded-lg ${borderSide} border-amber-500`}>
            <div className={`flex items-start gap-2 ${globalIsRTL ? 'flex-row-reverse text-right' : ''}`}>
              <span className="text-amber-600 text-lg">ℹ️</span>
              <div>
                <h3 className="font-bold text-amber-900 mb-1">{t('instructions.results')}</h3>
                <p className="text-amber-900 text-sm">{t('instructions.resultsText')}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="mt-6 space-y-2">
          <button
            onClick={() => onStart(testLanguage)}
            disabled={starting}
            className="w-full bg-secondary hover:bg-opacity-90 text-white font-bold py-3 rounded-lg text-base transition disabled:opacity-50"
          >
            {starting ? t('instructions.starting') : t('instructions.startNow')}
          </button>
          <button
            onClick={onClose}
            className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2.5 rounded-lg text-sm transition"
          >
            {t('instructions.cancel')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InstructionsModal;
